import React from "react";
import { useAppDispatch } from "index";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { Delete, CheckCircle, Info } from "@mui/icons-material";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { IconButton, Stack } from "@mui/material";
import type { ParsedExport } from "@store/data/types";
import type { ProviderDefinition } from "fileParsing/types";
import { removeData } from "@store/data/actions";
import useTranslations from "containers/hooks/useLanguageHook";
import { fileSizeToString } from "@/utils/format";

// Component-specific props.
type ComponentProps = {
  item: ProviderDefinition;
  export?: ParsedExport;
  onClick?: () => void;
  children?: React.ReactNode;
};

function ProviderSelectItem(props: ComponentProps) {
  const theme = useTheme();
  const { t } = useTranslations();
  const dispatch = useAppDispatch();

  return (
    <Box>
      <Box sx={{ m: 1 }}>
        {props.export ? (
          <Box>
            <Paper
              variant="outlined"
              sx={{
                margin: "0em",
                position: "relative",
                maxWidth: 300,
                minWidth: 300,
                minHeight: 180,
                maxHeight: 180,
                bgcolor: theme.palette.success.dark,
                color: theme.palette.primary.contrastText,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                boxSizing: "border-box",
              }}
            >
              <Typography
                className="title"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.25rem",
                  textAnchor: "middle",
                  pointerEvents: "none",
                }}
              >
                {props.item.id === "sample"
                  ? t.DataPage.SampleDataset
                  : props.item.id === "wrangled"
                    ? t.DataPage.UnknownProvider
                    : props.export.properties.providerName}
              </Typography>

              <Typography
                sx={{
                  mt: 0.75,
                  textAnchor: "middle",
                  fontSize: "0.9em",
                  fontFamily: "monospace",
                }}
              >
                {props.export?.fileProperties.fileName}
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  textAnchor: "middle",
                  fontSize: "1em",
                }}
              >
                {fileSizeToString(props.export?.fileProperties?.fileSize ?? 0)}
              </Typography>

              <Stack
                direction="row"
                gap={1}
                className="cta"
                sx={{
                  mt: 1.5,
                  alignItems: "center",
                  padding: 0.75,
                  borderRadius: 2,
                }}
              >
                <CheckCircle />
                <Typography
                  sx={{
                    textAnchor: "middle",
                    fontSize: "1em",
                    fontWeight: 600,
                  }}
                >
                  {t.DataPage.ImportedSuccessfully}
                </Typography>
              </Stack>

              <Tooltip title={<Typography variant="body1">{t.DataPage.DeleteExport}</Typography>} arrow>
                <IconButton
                  onClick={() => !!props.export && dispatch(removeData(props.export.id))}
                  sx={{
                    position: "absolute",
                    color: theme.palette.primary.contrastText,
                    right: 4,
                    top: 4,
                  }}
                >
                  <Delete />
                </IconButton>
              </Tooltip>

              {props.children}
            </Paper>
          </Box>
        ) : (
          <ButtonBase focusRipple onClick={() => props.onClick?.()}>
            <Paper
              variant="outlined"
              sx={{
                margin: "0em",
                position: "relative",
                maxWidth: 300,
                minWidth: 300,
                minHeight: 180,
                maxHeight: 180,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                padding: `0.5em`,
                boxSizing: "border-box",
                "&:hover": {
                  border: `1px solid ${theme.palette.primary.main}`,
                  ".cta": {
                    backgroundColor: theme.palette.primary.light,
                  },
                },
              }}
            >
              <Typography
                className="title"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.25rem",
                  textAnchor: "middle",
                  pointerEvents: "none",
                }}
              >
                {props.item.id === "sample"
                  ? t.DataPage.SampleDataset
                  : props.item.id === "wrangled"
                    ? t.DataPage.UnknownProvider
                    : props.item.name}
              </Typography>

              <Typography
                sx={{
                  mt: 0.75,
                  textAnchor: "middle",
                  fontSize: "1em",
                }}
              >
                {t.DataPage.NotImported}
              </Typography>

              <Stack
                direction="row"
                gap={1}
                className="cta"
                sx={{
                  mt: 2,
                  alignItems: "center",
                  padding: 0.75,
                  pr: 1,
                  borderRadius: 1,
                  bgcolor: theme.palette.secondary.main,
                  color: theme.palette.secondary.contrastText,
                }}
              >
                <Info />
                <Typography
                  sx={{
                    textAnchor: "middle",
                    fontSize: "1em",
                    fontWeight: 500,
                  }}
                >
                  {t.DataPage.HowToRequest}
                </Typography>
              </Stack>

              {props.children}
            </Paper>
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
}

export { ProviderSelectItem };
